import { createRouter, createWebHistory } from 'vue-router';
import { routes } from './routes';
import { trackPage } from '@/api/track.js';
import { useAuth } from '@/store/useAuth';

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/* ============================================================
   One guard, and it does two things in a fixed order.

   1. BOOT FIRST. The sign-in round trip is finished by the auth store, and it is the only reader of ?code/?state —
      nothing may decide whether someone is signed in before it has run. The store's boot() is idempotent, so this
      awaits the same promise on every navigation and costs nothing after the first — for every page but the room,
      which stands first and boots itself.
   2. THEN THE ADDRESS: what the round trip left in it is not written back (below). The room's own ways out — a
      stranger to the front door, a failed trip back to it with her line — are the room's (RoomView), as they were
      listen.js's: the room stands first.
   ============================================================ */
/* what the OAuth round trip leaves in the address, and what only finishLogin() may read */
const SPENT = ['code', 'state', 'error'];

/* the analytics' page name — the stable business name for the view, not its title (track.js): the four the documents
   used to report, and nothing for the bounce pages, which are not pages. The playlist's share page reports nothing
   (owner 2026-09-27: its GET /share/playlists/{id} is not live yet — docs/devTasks/TRACKING-decisions-2026-09-27.md) */
const PAGE: Record<string, string> = {
  home: 'home',
  'not-found': 'home' /* the front door at an address no page owns: index.html, as it was */,
  listen: 'listen',
  song: 'song',
  video: 'share',
};
/* which boot the document's page ran: the room finishes a round trip and asks for the profile itself (listen.js), a
   share page finishes a stale one quietly (song.js, playlist.js); the front door and the legal pages ask nothing */
const BOOT: Record<string, 'room' | 'share'> = { listen: 'room', song: 'share', video: 'share', playlist: 'share' };
/* THE PAGE IS COUNTED BEFORE ANYTHING ELSE RUNS (ROOM-A4: listen.js and front.js called initTrack at their top, so a
   document's $pageview came before its sign-in finish and its webLoginEnd). Once per page this document reaches: a
   redirect to the same page (the spent code) is not a second visit; one to another page is the new document the
   reference would have loaded there. */
let counted = '';
const countPage = (name: unknown) => {
  const page = PAGE[String(name || '')];
  if (!page || page === counted) return;
  counted = page;
  trackPage(page);
};

router.beforeEach(async (to) => {
  /* A BOUNCE IS NOT A PAGE. /x-callback and /google-callback hold the round trip's ?code for a moment and hand
     the whole address on with a full navigation; finishLogin() must run where the result will be USED. Booting
     here spent the landing on a page that was about to stop existing — the sign-in itself survived (the tokens
     are written), so it looked fine, while `is_new` and the front door's carry were silently lost with it. */
  if (to.meta.bounce) return true;
  countPage(to.name);
  const auth = useAuth();
  const kind = BOOT[String(to.name || '')] || 'quiet';
  /* THE ROOM STANDS BEFORE ITS FINISH (listen.html painted the room and listen.js inited it; only then did its boot
     `await signin.finish()`): the room boots ITSELF once it is up (RoomView) — the finish reads the trip's ?code from
     the address then and takes it out, and the room signs in or leaves by one of its two ways out. So nothing here may
     touch the room's address first. Every other page renders once its boot is done, as its document did. */
  if (kind === 'room') return true;
  await auth.boot({ page: kind });

  /* boot() has read ?code/?state and scrubbed them with replaceState — but the navigation that is resolving right
     now was computed from the address BEFORE that, so vue-router is about to write them back. Redirect to the same
     place without them: a reload must not find a spent code, and the address must not carry one in history. */
  if (SPENT.some((k) => k in to.query)) {
    const query = { ...to.query };
    for (const k of SPENT) delete query[k];
    return { path: to.path, query, hash: to.hash, replace: true };
  }

  return true;
});

/* The tab's name. Each address had its own <title> as an MPA and keeps it here — it is what a browser's history,
   a bookmark and a pinned tab all read, and an SPA that never changes it turns all eight into one entry. */
router.afterEach((to) => {
  countPage(to.name); /* a page reached by a redirect the guard did not see first (the not-found fallback) */
  const t = to.meta?.title;
  if (typeof t === 'string' && t) document.title = t;
});

export default router;
